
import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  getAdminUsers, 
  addAdminUser, 
  updateAdminUser, 
  removeAdminUser,
  getCurrentUserRole,
  getUserByEmail,
  getAdminAuditLog,
  AdminUser,
  AuditLogEntry
} from '../services/adminService';
import { toast } from 'sonner';

export const useSecureAdminManagement = () => {
  const [userRole, setUserRole] = useState<string | null>(null);
  const queryClient = useQueryClient();

  useEffect(() => {
    const checkUserRole = async () => {
      const role = await getCurrentUserRole();
      setUserRole(role);
    };
    checkUserRole();
  }, []);

  const { data: adminUsers, isLoading, error } = useQuery({
    queryKey: ['admin-users'],
    queryFn: getAdminUsers,
    enabled: userRole === 'admin',
  });

  const { data: auditLog } = useQuery({
    queryKey: ['admin-audit-log'],
    queryFn: getAdminAuditLog,
    enabled: userRole === 'admin',
  });

  const addAdminMutation = useMutation({
    mutationFn: async ({ 
      identifier, 
      role 
    }: { 
      identifier: string; 
      role: 'admin' | 'moderator' 
    }) => {
      // Check if identifier is email or UUID
      const isEmail = identifier.includes('@');
      
      if (isEmail) {
        // Find user by email first
        const user = await getUserByEmail(identifier);
        if (!user) {
          throw new Error('No user found with this email address');
        }
        return addAdminUser(user.id, role, identifier);
      } else {
        // Assume it's a UUID
        return addAdminUser(identifier, role);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      queryClient.invalidateQueries({ queryKey: ['admin-audit-log'] });
      toast.success('Admin/Moderator added successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to add admin/moderator');
    },
  });

  const updateAdminMutation = useMutation({
    mutationFn: ({ adminId, role }: { adminId: string; role: 'admin' | 'moderator' }) =>
      updateAdminUser(adminId, role),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      queryClient.invalidateQueries({ queryKey: ['admin-audit-log'] });
      toast.success('Role updated successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to update role');
    },
  });

  const removeAdminMutation = useMutation({
    mutationFn: (adminId: string) => removeAdminUser(adminId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      queryClient.invalidateQueries({ queryKey: ['admin-audit-log'] });
      toast.success('Admin/Moderator removed successfully');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to remove admin/moderator');
    },
  });

  return {
    adminUsers: adminUsers || [],
    auditLog: auditLog || [],
    isLoading,
    error,
    userRole,
    isAdmin: userRole === 'admin',
    addAdmin: addAdminMutation.mutate,
    updateAdmin: updateAdminMutation.mutate,
    removeAdmin: removeAdminMutation.mutate,
    isAdding: addAdminMutation.isPending,
    isUpdating: updateAdminMutation.isPending,
    isRemoving: removeAdminMutation.isPending,
  };
};
